import React from "react";
import { useDispatch, useSelector } from "react-redux";
import { useLazyGetItemByUniqueIDQuery } from "../../src/taskpane/components/_v2/redux/api/sharepoint/api-sharepoint-slice";
import { RootState } from "../../src/taskpane/components/_v2/redux/storeMain";
import { ComponentTypes } from "../../src/taskpane/config/config";
import { SharePointFolderContentTypeIds, sharePointInternalFileName } from "../../src/taskpane/config/constants";
import { SharePointMetadataBooleans } from "../../src/taskpane/config/sharepointConstants";
import { SharePointInputData } from "../../src/taskpane/interfaces/props/SharePointInputData";
import { DefaultValueService } from "../../src/taskpane/services/DefaultValueService";
import authModule from "../../utilities/AuthModule";
import { setDatasourceValues } from "../../src/taskpane/redux/modules/datasource";
import { IDocUrlDropdown } from "../../src/taskpane/interfaces/IDocUrlDropdown";
import { DatasourceValidationHelper } from "../../src/taskpane/components/helpers/datasourceValidationHelper";
import { FilePreCommitRequest, FolderPreCommitRequest } from "../../src/taskpane/components/_v2/redux/api/local/api-local-interfaces";
import { LanguageContext } from "../../utilities/LocalizationModule";
import { InfoPanelMode } from "../Panels/CMFileInfoPanel";
import generalModule from "../../utilities/GeneralModule";
import { logger } from "../../utilities/logger";
import { IFilePreCommitResult } from "../../src/taskpane/interfaces/IFilePreCommitResult";
import { CacheHelper } from "../../utilities/CacheHelper";
import { apiFilePreCommitCheck, apiFolderPreCommitCheck } from "../../src/taskpane/services/ApiService";
import { resolveDestinationUrl } from "../../src/taskpane/components/helpers/urlDestination";
import { FileFolderNameExtensions } from "../../src/taskpane/services/FileFolderNameExtensions";
import { refrehOptionsRequest } from "../../src/taskpane/config/datasourceConstants";
import { NewDocumentTemplate } from "../../src/taskpane/interfaces/IDocumentTemplate";
import { DatasourceHelper } from "../../src/taskpane/components/helpers/dataSourceHelper";
import { IContainerDisplayNames } from "../../utilities/ContainerHelper";

export function useMetaDataFormInputs(selectedItem: any, selectedDocumentLibrary?: IDocUrlDropdown, infoPanelMode?: InfoPanelMode, webAddinItem?: any, selectedTemplate?: NewDocumentTemplate, siteUrl?: string, listId?: string, containerDisplayNames?: IContainerDisplayNames) {
  const languageStrings = React.useContext(LanguageContext);

  const dispatch = useDispatch();

  const currentDirectory = useSelector((state: RootState) => state.workingDirectories?.workingDirectories?.length > 0 ? state.workingDirectories?.workingDirectories[state.workingDirectories?.workingDirectories?.length - 1] : null);
  const oneDriveRootDetails = useSelector((state: RootState) => state.workingDirectories?.oneDriveRootDetails);
  const datasourceValues = useSelector((state: RootState) => state.datasource?.values);
  const datasourceInitialLoad = useSelector((state: RootState) => state.datasource?.initialLoad);
  const datasourceCallerId = useSelector((state: RootState) => state.datasource?.callerId);
  const user = useSelector((state: RootState) => state.user);
  const requiredMetadataOnPublish = useSelector((state: RootState) => state.tenantInfo.requiredMetadataOnPublish);
  const datasourceItems = useSelector((state: RootState) => state.tenantInfo.datasources);

  const [inputValuesToSave, setInputValuesToSave] = React.useState<SharePointInputData[]>([]);
  const [itemWebUrl, setItemWebUrl] = React.useState<string>(null);
  const [itemSharepointListId, setItemSharepointListId] = React.useState<string>(null);
  const [fieldsLoaded, setFieldsLoaded] = React.useState<boolean>(false);
  const [extention, setExtention] = React.useState('');

  const [getItemByUniqueId] = useLazyGetItemByUniqueIDQuery();

  const checkIfInOneDrive = () => {
    if (currentDirectory) {
      return currentDirectory?.type == ComponentTypes.OneDrive || currentDirectory?.type == ComponentTypes.OneDriveSubFolder
    }
    else if (user?.oneDriveWebUrl && webAddinItem?.webUrl?.startsWith(user.oneDriveWebUrl)) {
      return true;
    }
    else if ((webAddinItem?.webUrl || webAddinItem?.weburl)?.indexOf("sharepoint.com/personal/") > -1) {
      return true;
    }
    return false;
  }

  const isInOneDrive = React.useRef(checkIfInOneDrive());
  const firstLoad = React.useRef(true);

  React.useEffect(() => {
    if (datasourceValues) {
      updateMetadataPropertiesFromDataSource(datasourceValues);
    }
  }, [datasourceValues]);

  React.useEffect(() => {
    if (siteUrl && listId) {
      setItemWebUrl(siteUrl);
      setItemSharepointListId(listId);
    }
    else {
      if (isInOneDrive.current) {
        let siteUrl = webAddinItem ? webAddinItem.sharepointIds.siteUrl : oneDriveRootDetails?.sharePointSiteUrl;
        let sharePointListId = webAddinItem ? webAddinItem.sharepointIds.listId : oneDriveRootDetails?.sharePointListId;

        setItemWebUrl(siteUrl);
        setItemSharepointListId(sharePointListId);
      } else {
        let webUrl = itemWebUrl;
        let sharePointListId = itemSharepointListId;

        if (selectedDocumentLibrary) {
          webUrl = selectedDocumentLibrary.webUrl;
          sharePointListId = selectedDocumentLibrary.sharePointListId;
        }
        else if (webAddinItem) {
          webUrl = webAddinItem.sharepointIds.siteUrl;
          sharePointListId = webAddinItem.sharepointIds.listId;
        }
        else {
          webUrl = currentDirectory?.webUrl;
          sharePointListId = currentDirectory?.sharePointListId;
        }

        setItemWebUrl(webUrl);
        setItemSharepointListId(sharePointListId);
      }
    }
  }, [siteUrl, listId]);

  const updateMetadataPropertiesFromDataSource = (values: any) => {
    const isMultipleValues: boolean = Array.isArray(values) && values.length > 0;
    const propertyKeys = isMultipleValues
      ? Object.keys(values[0])
      : Object.keys(values);

    if (propertyKeys.length > 0) {
      const updatedinputValuesToSave = [...inputValuesToSave];
      propertyKeys.forEach((propKey) => {
        let updatedValue: any;
        if (isMultipleValues) {
          const uniqueValues = values
            .map(value => value[propKey])
            .filter((value, index, self) => self.indexOf(value) === index);
          updatedValue = uniqueValues;
        }
        else {
          updatedValue = values[propKey];
        }
        updateDatasourceDisplayNameInputData(updatedinputValuesToSave, propKey, updatedValue, isMultipleValues);
      });
      //updateSharepointMetadataToSave(updatedinputValuesToSave, informationProtectionId);
      updateSharepointMetadataToSave(updatedinputValuesToSave, null);
    }
    dispatch(setDatasourceValues(null));
  };

  const updateDatasourceDisplayNameInputData = (
    updatedinputValuesToSave: SharePointInputData[],
    propKey: string,
    value: any,
    isMultipleValues: boolean
  ) => {
    let inputDatasourceId: number;
    let inputToUpdate = inputValuesToSave.find(
      (item) => {
        const boundName: string = (item?.xml?.ContentTypeInputName ?? item?.displayName)?.trim().toLowerCase();
        // return boundName && (item.internalName !== sharePointInternalFileName || item?.xml?.ContentTypeInputName) && boundName === propKey.trim().toLowerCase()
        if (boundName && (item.internalName !== sharePointInternalFileName || item?.xml?.ContentTypeInputName) && boundName === propKey.trim().toLowerCase()) {
          inputDatasourceId = datasourceCallerId && DatasourceHelper.getInputDataSource(datasourceItems, boundName, [containerDisplayNames?.siteDisplayName ?? currentDirectory?.siteDisplayName ?? ""], [containerDisplayNames?.libraryDisplayName ?? currentDirectory?.DocumentLibrary ?? currentDirectory?.sharepointList ?? ""])?.id;
          return true;
        }
        return false;
      }
    );

    if (inputToUpdate) {
      const inputDatasourceCompatible: boolean = Number.isInteger(inputDatasourceId) && inputDatasourceId === datasourceCallerId;
      if (value === refrehOptionsRequest) {
        const updatedInput: SharePointInputData = inputDatasourceCompatible && updatedinputValuesToSave[inputToUpdate.index];
        if (updatedInput) {
          updatedInput.resetDatasourceOptionsToggle = !updatedInput.resetDatasourceOptionsToggle;
        }
      }
      else if (isMultipleValues || inputDatasourceCompatible) {
        try {
          const updatedInput: SharePointInputData = inputDatasourceCompatible && updatedinputValuesToSave[inputToUpdate.index];
          if (updatedInput) {
            if (!Array.isArray(value)) {
              value = [value];
            }
            if (updatedInput.value) {
              if (Array.isArray(updatedInput.value) && updatedInput.value.length > 0) {
                updatedInput.value = updatedInput.value
                  .map(val => {
                    if (val && value.some(x => x?.trim().toLowerCase() === val.trim().toLowerCase())) {
                      return val;
                    }
                    return null;
                  })
                  .filter(val => val);
              }
              else if (!datasourceInitialLoad) {
                if (value.length === 1) {
                  DatasourceValidationHelper.setSingleDatasourceResultValue(updatedinputValuesToSave, inputToUpdate, value[0]);
                }
                else if ((value.length > 1 && !value.some(x => x?.trim().toLowerCase() === updatedInput?.value?.trim().toLowerCase()))) {
                  updatedInput.value = null;
                }
              }
            }
            else if (value.length === 1) {
              DatasourceValidationHelper.setSingleDatasourceResultValue(updatedinputValuesToSave, inputToUpdate, value[0]);
            }
            updatedInput.updatedDatasourceOptions = value;
          }
        } catch (error) {
          logger(error, "useMetadataFormsInput.ts (updateDatasourceDisplayNameInputData)");
        }
      }
      else {
        DatasourceValidationHelper.setSingleDatasourceResultValue(updatedinputValuesToSave, inputToUpdate, value);
      }
    }
  };

  const mapColumnsToSaveFieldsOneDrive = async () => {
    try {
      let fields = [{
        Choices: undefined,
        CurrentValue: null,
        DefaultValue: null,
        Id: "5cc6dc79-3710-4374-b433-61cb4a686c12",
        InternalName: sharePointInternalFileName,
        isValid: true,
        xml: {
          ColName: "nvarchar8",
          DisplayName: languageStrings.Name,
          ID: "{5cc6dc79-3710-4374-b433-61cb4a686c12}",
          Name: "Name",
          ReadOnly: infoPanelMode === InfoPanelMode.Replace ? "TRUE" : "FALSE",
          Required: "TRUE",
          Sealed: "TRUE",
          ShowInEditForm: "TRUE",
          ShowInFileDlg: "FALSE",
          ShowInNewForm: "FALSE",
          SourceID: "http://schemas.microsoft.com/sharepoint/v3",
          StaticName: "Name",
          Type: "Text",
        }
      },
        // {
        //   Choices: undefined,
        //   CurrentValue: null,
        //   DefaultValue: null,
        //   Id: "5cc6dc79-3710-4374-b433-61cb4a686c12",
        //   InternalName: "Title",
        //   isValid: true,
        //   xml: {
        //     ColName: "nvarchar8",
        //     DisplayName: languageStrings.Title,
        //     ID: "{5cc6dc79-3710-4374-b433-61cb4a686c12}",
        //     Name: "Title",
        //     Required: "FALSE",
        //     Sealed: "TRUE",
        //     ShowInEditForm: "TRUE",
        //     ShowInFileDlg: "FALSE",
        //     ShowInNewForm: "FALSE",
        //     SourceID: "http://schemas.microsoft.com/sharepoint/v3",
        //     StaticName: "Title",
        //     Type: "Text",
        //   }
        // }
      ];

      if (!selectedTemplate) {
        let file: any;
        let extention: string;

        try {
          file = await getItemByUniqueId({
            siteUrl: itemWebUrl,
            sharePointListId: itemSharepointListId,
            uniqueId: webAddinItem ? webAddinItem.sharepointIds.listItemUniqueId : selectedItem?.UniqueId.replace("{", "").replace("}", ""),
            accessToken: await authModule.getOneDriveAccessToken(),
          }).unwrap();
        } catch (error) {
          logger(error, "useMetadataFormInputs.tsx (getItemByUniqueId)");
        }

        const isFolderType: boolean = file?.ContentTypeId?.startsWith(SharePointFolderContentTypeIds.folder);

        let inputSaveFields: SharePointInputData[] = await Promise.all(
          fields.map(async (item, index) => {
            try {

              let defaultValue: any;
              let nameValue = webAddinItem ? webAddinItem.name : selectedItem?.FileLeafRef;

              if (item.InternalName === sharePointInternalFileName) {
                const fileParts = !isFolderType
                  ? nameValue?.split('.')
                  : [];
                if (Array.isArray(fileParts) && fileParts.length > 1) {
                  defaultValue = fileParts.slice(0, -1).join('.');
                  extention = fileParts[fileParts.length - 1];
                  setExtention(extention);
                } else {
                  defaultValue = nameValue;
                }
              } else {
                defaultValue = file[item.InternalName];
              }

              let initialIsRequiredAndHasNoDefaultValue: boolean;

              if (!defaultValue) {
                initialIsRequiredAndHasNoDefaultValue = item.xml.Required === SharePointMetadataBooleans.true && !item.DefaultValue;
                defaultValue = DefaultValueService.setValue(item.xml.Type, item.DefaultValue);
              }

              return {
                index: index,
                internalName: item.InternalName,
                value: defaultValue,
                hasError: initialIsRequiredAndHasNoDefaultValue,
                type: item.xml.Type,
                displayName: item.xml.DisplayName,
                xml: item.xml,
                choices: item.Choices,
                extention: item.InternalName === sharePointInternalFileName && !isFolderType ? extention : null
              };
            } catch (error) {
              logger(error, "useMetadataFormInputs.tsx (OneDrive fields map)");
            }

            return null;
          })
        );

        setInputValuesToSave(inputSaveFields);
      } else {
        let inputSaveFields: SharePointInputData[] =
          fields.map((item, index) => {
            try {
              let initialValue: any;

              const initialValueIsRequired: boolean = (!initialValue || (Array.isArray(initialValue) && initialValue?.length === 0)) && item.xml.Required === SharePointMetadataBooleans.true;

              return {
                index: index,
                internalName: item.InternalName,
                value: initialValue,
                hasError: initialValueIsRequired,
                type: item.xml.Type,
                displayName: item.xml.DisplayName,
                xml: item.xml,
                choices: item.Choices,
              };
            } catch (error) {
              logger(error, "useMetadataFormInputs.tsx (fields map)");
            }

            return null;
          });

        if (requiredMetadataOnPublish) {
          addRequiredMetadataToCache(inputSaveFields);
        }
        setInputValuesToSave(inputSaveFields);
      }

    } catch (error) {
      logger(error, "useMetadataFormInputs.tsx (mapColumnsToSaveFieldsOneDrive)");
    }
  };

  const mapColumnsToSaveFields = async (fields: any[], inputValues?: SharePointInputData[]) => {
    try {
      if (!selectedTemplate) {
        let extention: string;

        let file: any = await getItemByUniqueId({
          siteUrl: itemWebUrl,
          sharePointListId: itemSharepointListId,
          uniqueId: webAddinItem ? webAddinItem?.sharepointIds?.listItemUniqueId : selectedItem?.uniqueid || selectedItem.UniqueId?.replace("{", "").replace("}", ""),
          accessToken: isInOneDrive.current ? await authModule.getOneDriveAccessToken() : await authModule.getSharePointAccessToken(),
        }).unwrap();

        const isFolderType: boolean = file?.ContentTypeId?.startsWith(SharePointFolderContentTypeIds.folder);

        let fileParts = [];
        if (!isFolderType) {
          if (selectedItem) {
            fileParts = selectedItem.name ? selectedItem.name.split('.') : selectedItem.FileLeafRef?.split('.');
          }
          else if (webAddinItem) {
            fileParts = webAddinItem.name?.split('.');
          }
        }

        let fileNameWithOutExtension: string;
        if (Array.isArray(fileParts) && fileParts.length > 0) {
          if (fileParts.length > 1) {
            fileNameWithOutExtension = fileParts.slice(0, -1).join('.');
            extention = fileParts[fileParts.length - 1];
            setExtention(extention);
          }
          else {
            fileNameWithOutExtension = fileParts[0];
          }
        } else {
          fileNameWithOutExtension = selectedItem
            ? selectedItem?.name ?? selectedItem?.FileLeafRef
            : webAddinItem.name;
        }

        let inputSaveFields: SharePointInputData[] =
          fields.map((item, index) => {
            try {
              let initialValue: any;
              if (item.InternalName === sharePointInternalFileName) {
                initialValue = fileNameWithOutExtension;
              }
              else if (item.xml.Type === 'URL' && typeof file[item.InternalName] === 'object') {
                initialValue = file[item.InternalName] ? file[item.InternalName]?.Url : null;
              }
              else if (item.xml.Type === 'TaxonomyFieldType' && typeof file[item.InternalName] === 'object') {
                initialValue = file[item.InternalName] ? [file[item.InternalName]?.Label] : null;
              }
              else if (item.xml.Type === 'Lookup') {
                initialValue = file[`${item.InternalName}Id`] ? [file[`${item.InternalName}Id`]] : null;
              }
              else if (item.xml.Type === 'LookupMulti') {
                initialValue = file[`${item.InternalName}Id`] && file[`${item.InternalName}Id`]?.length > 0 ? file[`${item.InternalName}Id`] : null;
              }
              else if (item.xml.Type === 'User') {
                initialValue = file[`${item.InternalName}Id`] ? [file[`${item.InternalName}Id`]] : null;
              }
              else if (item.xml.Type === 'UserMulti') {
                // initialValue = file[`${item.InternalName}Id`] && file[`${item.InternalName}Id`]?.length > 0 ? file[`${item.InternalName}Id`] : null;
                const fieldValue = file[`${item.InternalName}Id`];
                if (Array.isArray(fieldValue)) {
                  initialValue = fieldValue.length > 0
                    ? fieldValue
                    : null;
                }
                else if (fieldValue && parseInt(fieldValue)) {
                  initialValue = fieldValue;
                }
              }
              else if (item.xml.Type === "DateTime") {
                let dateValue: Date = file[item.InternalName] && file[item.InternalName]?.length > 0 && new Date(file[item.InternalName]);
                if (dateValue) {
                  dateValue = new Date(dateValue.getFullYear(), dateValue.getMonth(), dateValue.getDate(), dateValue.getHours(), dateValue.getMinutes(), dateValue.getSeconds(), dateValue.getMilliseconds());
                }
                initialValue = dateValue || null;
              }
              else {
                initialValue = file[item.InternalName] ?? null;
              }

              const initialValueIsRequired: boolean = (!initialValue || (Array.isArray(initialValue) && initialValue?.length === 0)) && item.xml.Required === SharePointMetadataBooleans.true;

              return {
                index: index,
                internalName: item.InternalName,
                value: initialValue,
                hasError: initialValueIsRequired,
                type:
                  item.xml.Percentage === SharePointMetadataBooleans.true
                    ? `${item.xml.Type}Percentage`
                    : item.xml.Type,
                displayName: item.xml.DisplayName,
                xml: item.xml,
                choices: item.Choices,
                extention: item.InternalName === sharePointInternalFileName ? extention : null
              };
            } catch (error) {
              logger(error, "useMetadataFormInputs.tsx (fields map)");
            }

            return null;
          });

        if (requiredMetadataOnPublish) {
          addRequiredMetadataToCache(inputSaveFields);
        }

        setInputValuesToSave(inputSaveFields);
      } else {
        let inputSaveFields: SharePointInputData[] =
          fields.map((item, index) => {
            try {
              const populatedInput = inputValues?.find(x => x.internalName === item.InternalName);

              if (populatedInput) {
                return populatedInput;
              } else {
                let initialValue: any;

                const initialValueIsRequired: boolean = (!initialValue || (Array.isArray(initialValue) && initialValue?.length === 0)) && item.xml.Required === SharePointMetadataBooleans.true;

                return {
                  index: index,
                  internalName: item.InternalName,
                  value: initialValue,
                  hasError: initialValueIsRequired,
                  type:
                    item.xml.Percentage === SharePointMetadataBooleans.true
                      ? `${item.xml.Type}Percentage`
                      : item.xml.Type,
                  displayName: item.xml.DisplayName,
                  xml: item.xml,
                  choices: item.Choices,
                };
              }
            } catch (error) {
              logger(error, "useMetadataFormInputs.tsx (fields map)");
            }

            return null;
          });

        if (requiredMetadataOnPublish) {
          addRequiredMetadataToCache(inputSaveFields);
        }
        setInputValuesToSave(inputSaveFields);
      }

    } catch (error) {
      logger(error, "useMetadataFormInputs.tsx (mapColumnsToSaveFields)");
    }
  };

  const addRequiredMetadataToCache = (inputValues?: SharePointInputData[]) => {
    if (inputValues && requiredMetadataOnPublish) {
      const requiredMetaFieldsInternalNames: string[] = requiredMetadataOnPublish.split(",");
      for (let index = 0; index < inputValues.length; index++) {
        const inputValue = inputValues[index];
        if (inputValue && requiredMetaFieldsInternalNames.some(x => x.toLowerCase() === inputValue.displayName.toLowerCase())) {
          let valueToCache: any;
          if (inputValue.type === "NumberPercentage") {
            valueToCache = inputValue.value ? (Number(inputValue.value) * 100).toString() : "";
          }
          else if (inputValue.type === "TaxonomyFieldTypeMulti") {
            valueToCache = Array.isArray(inputValue.value) ? inputValue.value.map(x => x.Label).join(",") : "";
          }
          else {
            valueToCache = inputValue.value;
          }

          CacheHelper.set(inputValue.internalName, valueToCache ? JSON.stringify(valueToCache.toString()) : "");
        }
      }
    }
  }

  const updateSharepointMetadataToSave = (metadata: SharePointInputData[], informationProtectionId: string) => {
    try {
      if (isInOneDrive.current) {
        setInputValuesToSave(metadata);
      }
      else if (!firstLoad.current) {
        setInputValuesToSave(metadata);
      }
      else {
        firstLoad.current = false;
      }

    } catch (error) {
      logger(error, "useMetadataFormInputs.tsx (updateSharepointMetadataToSave)");
    }
  }

  const validateName = async (input: SharePointInputData, addInItem: any, forceNameCheck: boolean = false) => {
    const sanitizedName: string = FileFolderNameExtensions.EnsureValidName(input.value, true, !forceNameCheck);
    if (!sanitizedName) {
      input.hasError = true;
      input.errorMessage = languageStrings.FieldRequired;
      return;
    }

    let isFolder = false;

    if (generalModule.isInConentMangerMode()) {
      if (infoPanelMode !== InfoPanelMode.NewTemplate) {
        isFolder = selectedDocumentLibrary ? selectedItem.contenttypeid?.startsWith(SharePointFolderContentTypeIds.folder) : selectedItem.ContentTypeId?.startsWith(SharePointFolderContentTypeIds.folder)
      }
    }
    else {
      isFolder = addInItem?.contenttypeid?.startsWith(SharePointFolderContentTypeIds.folder);
    }

    let siteUrl: string;
    let destinationUrl: string;
    let originalFileName: string;
    let newFileName = infoPanelMode === InfoPanelMode.NewTemplate ? `${sanitizedName}.${selectedTemplate.extention}` : `${sanitizedName}${extention ? `.${extention}` : ''}`;

    if (webAddinItem) {
      siteUrl = webAddinItem.sharepointIds.siteUrl;
      destinationUrl = (webAddinItem.webUrl || webAddinItem.weburl).replace(`/${encodeURI(webAddinItem.name)}`, '');
      originalFileName = webAddinItem.name;
    }
    else if (selectedDocumentLibrary) {
      siteUrl = selectedDocumentLibrary.webUrl;
      destinationUrl = selectedItem.weburl.replace(`/${selectedItem.name}`, '');
      originalFileName = selectedItem.name;
    }
    else {
      siteUrl = isInOneDrive.current ? oneDriveRootDetails.sharePointSiteUrl : currentDirectory?.webUrl;

      destinationUrl = await resolveDestinationUrl(currentDirectory, { isOneDrive: isInOneDrive.current });

      originalFileName = infoPanelMode !== InfoPanelMode.NewTemplate ? (selectedItem?.FileLeafRef ?? "") : "";
    }

    let results: IFilePreCommitResult;

    if (!isFolder) {
      const isNameChanged: boolean = originalFileName && originalFileName.toLowerCase().trim() !== newFileName.toLowerCase().trim();

      const fileRequest: FilePreCommitRequest = {
        accessToken: isInOneDrive.current ? await authModule.getOneDriveAccessToken() : await authModule.getSharePointAccessToken(),
        destinationUrl,
        siteUrl,
        fileName: newFileName,
        autoFolderCreation: false,
        ignoreSameUserLock: !isNameChanged
      }

      if (isNameChanged) {
        // Check if current file is locked or checked out.
        results = await apiFilePreCommitCheck({ ...fileRequest, fileName: originalFileName });
        if (results?.checkedOut) {
          input.hasError = true;
          input.errorMessage = languageStrings.FileSameCheckedOut;
          return;
        }
        else if (results?.locked) {
          input.hasError = true;
          input.errorMessage = languageStrings.FileSameLocked;
          return;
        }
      }

      results = await apiFilePreCommitCheck(fileRequest);

      if (results?.exists && isNameChanged) {
        input.hasError = true;
        input.errorMessage = languageStrings.FileSameName;
      }
      else if (results?.checkedOut) {
        input.hasError = true;
        input.errorMessage = languageStrings.FileSameCheckedOut;
      }
      else if (results?.locked) {
        input.hasError = true;
        input.errorMessage = languageStrings.FileSameLocked;
      }
      else {
        input.hasError = false;
        input.errorMessage = '';
      }
    } else {
      const folderRequest: FolderPreCommitRequest = {
        accessToken: isInOneDrive.current ? await authModule.getOneDriveAccessToken() : await authModule.getSharePointAccessToken(),
        destinationUrl,
        siteUrl,
        folderName: newFileName
      }

      results = await apiFolderPreCommitCheck(folderRequest);
      if (results?.exists && originalFileName.toLowerCase() !== newFileName.toLowerCase()) {
        input.hasError = true;
        input.errorMessage = languageStrings.FolderSameName;
      }
      else {
        input.hasError = false;
        input.errorMessage = '';
      }
    }
  }

  return {
    inputValuesToSave,
    itemSharepointListId,
    itemWebUrl,
    extention,
    fieldsLoaded,
    updateSharepointMetadataToSave,
    mapColumnsToSaveFields,
    mapColumnsToSaveFieldsOneDrive,
    validateName,
    setFieldsLoaded,
    checkIfInOneDrive,
    setInputValuesToSave
  }
}
